// Phase 2 of the GPU raymarch renderer (#3): a fragment shader that DDA-traverses
// the work-volume 3D texture (built by voxel-volume.ts) and shades the first
// solid voxel — no triangle meshing. An edit becomes a texture re-upload, not a
// re-mesh, which is what keeps MagicaVoxel responsive on huge brushes.
//
// EXPERIMENTAL, behind the ?raymarch flag. The DDA mirrors the CPU pick in
// voxel-pick.ts. Not yet at feature parity (no grid/edge lines, single light) —
// this is the visual-iteration starting point.

import * as THREE from "three";
import { CELL_SIZE, parseChunkKey } from "../model/voxel-grid";
import { type VoxelGrid } from "../model/voxel-grid";
import { type CellBounds } from "../interaction/pick";
import { type Selection } from "../model/selection";
import { type PaletteLookup } from "./voxel-mesh";
import {
  type VolumeData,
  type CoarseData,
  buildVolumeData,
  buildCoarseData,
  buildPaletteData,
  updateVolumeChunk,
} from "./voxel-volume";

// Scratch for updateCamera's per-frame local-direction transform (no alloc).
const _invMeshMat = new THREE.Matrix4();

const VERT = /* glsl */ `
out vec3 vLocalPos;
void main() {
  vLocalPos = position; // mesh-local metres; mesh origin = the volume's min corner
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;
precision highp sampler3D;

uniform sampler3D uVolume;   // R8 palette index per cell
uniform sampler2D uPalette;  // 256x1 RGBA
uniform vec3 uCameraLocal;   // camera in mesh-local metres (perspective eye point)
uniform float uOrtho;        // 1 = orthographic (parallel rays), 0 = perspective
uniform vec3 uViewDirLocal;  // ortho: constant view direction in mesh-local space
uniform vec3 uDims;          // w,h,d cells (cell-space bounds)
uniform vec3 uTexDims;       // texW,h,d — texture size (texW padded to mult of 4)
uniform vec3 uModelMin;      // occupied AABB min (cell space) — DDA clip
uniform vec3 uModelMax;      // occupied AABB max, exclusive (cell space)
uniform sampler3D uCoarse;   // per-chunk occupancy (R8) for empty-chunk skipping
uniform vec3 uCoarseTexDims; // texCw,ch,cd of uCoarse
uniform vec3 uChunkOrigin;   // min chunk coords the coarse texture starts at
uniform vec3 uVolumeOrigin;  // volume origin in grid cell coords (minIx,minIy,minIz)
uniform float uCellSize;
uniform vec3 uLightDir;
// Three.js auto-declares these for the vertex stage only; we also need them in
// the fragment stage to write gl_FragDepth. The renderer still sets them by name.
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform float uShowGrid;     // 1 = draw per-cell grid lines on hit faces
uniform float uShowEdges;    // 1 = draw silhouette edge lines
uniform float uGridFade;     // zoom fade for grid lines (matches the mesh)
uniform float uEdgeFade;     // zoom fade for edge lines
uniform sampler3D uSelection; // R8, 255 = this cell is selected (volume layout)
uniform float uHasSelection;  // 0 = nothing selected (skip the sample entirely)

in vec3 vLocalPos;
out vec4 outColor;

// Analytic anti-aliased per-cell grid lines: the distance to the nearest cell
// boundary is measured in PIXELS (dist / fwidth), then drawn as a constant
// ~1.2px line. Setting the width in screen pixels keeps the lines thin and
// identical at every viewing angle (no view-dependent thickness), needs no
// supersampling, and fades them out smoothly once cells shrink past a pixel —
// the same behaviour as the mesh's MSAA line geometry. Returns coverage [0,1].
float gridFactor(vec2 uv) {
  vec2 dist = abs(fract(uv + 0.5) - 0.5);  // 0 at a boundary, 0.5 at cell centre
  vec2 pd = dist / (fwidth(uv) + 1e-6);    // distance to the line, in pixels
  // 1-device-px AA line (no solid core), matching the mesh's 1px geometry lines
  // which the GPU's MSAA blends thin — a wider/solid core reads bolder than them.
  vec2 line = vec2(1.0) - smoothstep(vec2(0.0), vec2(1.0), pd);
  return max(line.x, line.y);
}

// Occupancy at a cell: 0 = empty / out of bounds, else the palette index / 255.
float occ(ivec3 c) {
  if (c.x < 0 || c.y < 0 || c.z < 0 ||
      c.x >= int(uDims.x) || c.y >= int(uDims.y) || c.z >= int(uDims.z)) return 0.0;
  return texture(uVolume, (vec3(c) + 0.5) / uTexDims).r;
}

// Silhouette-edge coverage for one in-plane axis at the hit cell. Mirrors the
// mesher: the edge across the near boundary is a silhouette iff the in-plane
// neighbour C' is empty, OR (C' solid AND C'+N solid — a step up). Drawn as a
// constant ~1.6px line via pixel-distance, like the grid.
float edgeAxis(ivec3 cell, ivec3 N, ivec3 ax, float uvc, float fwc) {
  float f = fract(uvc);
  int dir = (f < 0.5) ? -1 : 1;
  ivec3 Cp = cell + ax * dir;
  bool silh = (occ(Cp) < 0.002) || (occ(Cp + N) > 0.002);
  if (!silh) return 0.0;
  float distCells = (dir < 0) ? f : (1.0 - f);
  float pd = distCells / (fwc + 1e-6);
  return 1.0 - smoothstep(0.0, 1.1, pd); // 1px AA edge line to match the mesh
}

// Linear → sRGB (the renderer outputs sRGB; built-in materials encode this in
// their shader, a raw ShaderMaterial must do it itself or it reads dark).
vec3 linearToSRGB(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055,
             step(vec3(0.0031308), c));
}

// Trace ONE ray through the volume for the fragment-local position fragLocal
// (mesh-local metres). Returns true on a voxel hit, with the shaded LINEAR colour,
// the hit's NDC depth, and the hit's view-space depth in metres (for the
// supersample gate — NDC depth is useless there, see main()); false when the ray
// misses (caller treats as background). main() calls this several times per pixel
// to supersample.
bool traceFrag(vec3 fragLocal, out vec3 outCol, out float outDepth, out float outViewZ) {
  // Ray in CELL space (unit cubes). Perspective: origin = the camera eye, dir
  // from the eye to this back-face fragment (divergent rays). Orthographic: all
  // rays are PARALLEL — direction is the constant view dir, and each ray passes
  // through its own fragment. We back the origin off along the view dir by the
  // full volume diagonal so the model AABB is always AHEAD (tEnter > 0), keeping
  // the DDA structure below identical to the perspective path. Without this the
  // shader always treats the camera as a point eye, so panning the ortho camera
  // (which only moves uCameraLocal sideways) reintroduces perspective skew.
  vec3 ro, rd;
  if (uOrtho > 0.5) {
    rd = normalize(uViewDirLocal);
    float backoff = length(uDims) * uCellSize + uCellSize;
    ro = (fragLocal - rd * backoff) / uCellSize;
  } else {
    ro = uCameraLocal / uCellSize;
    rd = normalize(fragLocal - uCameraLocal);
  }
  vec3 rds = vec3(
    abs(rd.x) < 1e-6 ? 1e-6 : rd.x,
    abs(rd.y) < 1e-6 ? 1e-6 : rd.y,
    abs(rd.z) < 1e-6 ? 1e-6 : rd.z
  );

  // Re-base the ray origin to NEAR the model BEFORE any intersection math. ro
  // starts in cell space at the camera, which at a big canvas + zoomed out is
  // hundreds–thousands of cells away; a grazing ray then reaches the box at a huge
  // tEnter, and float32 turns that into ~1 cell of error in the entry position on
  // the non-grazing axes → the entry cell flips in/out of the model → the
  // silhouette speckles, worse the more cells the canvas has (the "culling that
  // gets worse with voxel density"). Sliding ro down the ray keeps every
  // downstream coordinate (tEnter, pos, tMax) small and exact.
  //
  // Slide to JUST BEFORE the near face — the closest approach to the model centre
  // MINUS the bounding-sphere radius — and never backwards (max(...,0)). Sliding
  // all the way to the closest approach lands ro INSIDE the box, so the DDA starts
  // mid-box and drops the near half (a spherical bite out of the model facing the
  // camera). Stopping a full radius short keeps ro outside, so tEnter stays > 0 and
  // the whole box is marched.
  vec3 boxCenter = 0.5 * (uModelMin + uModelMax);
  float boxRadius = 0.5 * length(uModelMax - uModelMin);
  ro += rds * max(dot(boxCenter - ro, rds) - boxRadius, 0.0); // rd unit-length → dot = distance

  // Clip the ray to the MODEL's occupied AABB (not the whole work volume) — the
  // empty-space skip: a small model in a big canvas only marches its own bounds.
  vec3 t0 = (uModelMin - ro) / rds;
  vec3 t1 = (uModelMax - ro) / rds;
  vec3 tlo = min(t0, t1);
  vec3 thi = max(t0, t1);
  float tEnter = max(max(tlo.x, tlo.y), tlo.z);
  float tExit = min(min(thi.x, thi.y), thi.z);
  if (tExit < max(tEnter, 0.0)) return false;

  float tStart = max(tEnter, 0.0);
  vec3 pos = ro + rds * (tStart + 1e-4);
  ivec3 lo = ivec3(uModelMin);
  ivec3 hi = ivec3(uModelMax);
  // The ray passed the AABB test, so its entry point IS inside [lo,hi) — but on a
  // grazing axis floor(pos) can round up to hi (a hair past the slab), which the
  // loop reads as out-of-bounds and the ray instantly misses a solid model →
  // dark speckle along the grazing-face edges. Clamp the ENTRY cell back into the
  // valid range; the re-base already keeps the error sub-cell, so this only nudges
  // the exact-boundary case, never a genuine miss (those returned above).
  ivec3 cell = clamp(ivec3(floor(pos)), lo, hi - ivec3(1));

  vec3 stp = sign(rds);
  vec3 tDelta = abs(1.0 / rds);
  vec3 nextB = vec3(cell) + max(stp, vec3(0.0));
  // tMax is the ray param (measured from ro) of the next cell boundary on each
  // axis. It MUST be relative to ro, not pos — tHit is read back as ro+rds*tHit
  // for grid lines, so a pos-relative tMax shifts the hit point on any voxel
  // reached after the first DDA step (e.g. a drawn box inside empty space).
  vec3 tMax = (nextB - ro) / rds;

  // Entry-face normal (which AABB slab we crossed).
  vec3 nrm = (tEnter == tlo.x) ? vec3(-stp.x, 0.0, 0.0)
           : (tEnter == tlo.y) ? vec3(0.0, -stp.y, 0.0)
                               : vec3(0.0, 0.0, -stp.z);

  float tHit = tStart; // t at which the ray entered the current cell
  for (int i = 0; i < 1024; i++) {
    if (cell.x < lo.x || cell.y < lo.y || cell.z < lo.z ||
        cell.x >= hi.x || cell.y >= hi.y || cell.z >= hi.z) break;
    // Empty-chunk skip: if this cell's 16³ chunk holds no voxels, jump straight
    // to the chunk's exit face along the ray instead of stepping cell by cell.
    ivec3 gchunk = ivec3(floor((vec3(cell) + uVolumeOrigin) / 16.0));
    if (texture(uCoarse, (vec3(gchunk - ivec3(uChunkOrigin)) + 0.5) / uCoarseTexDims).r < 0.5) {
      vec3 exitVol = vec3(gchunk * 16) + 16.0 * step(vec3(0.0), rds) - uVolumeOrigin;
      vec3 tc = (exitVol - ro) / rds;
      float tj = min(min(tc.x, tc.y), tc.z) + 1e-3;
      nrm = (tc.x <= tc.y && tc.x <= tc.z) ? vec3(-stp.x, 0.0, 0.0)
          : (tc.y <= tc.z)                 ? vec3(0.0, -stp.y, 0.0)
                                           : vec3(0.0, 0.0, -stp.z);
      tHit = tj;
      cell = ivec3(floor(ro + rds * tj));
      tMax = (vec3(cell) + max(stp, vec3(0.0)) - ro) / rds;
      continue;
    }
    float p = floor(texture(uVolume, (vec3(cell) + 0.5) / uTexDims).r * 255.0 + 0.5);
    if (p > 0.5) {
      vec3 base = texture(uPalette, vec2((p + 0.5) / 256.0, 0.5)).rgb;
      // Approximate the mesh editing rig (bright ambient + a white-sky/grey-ground
      // hemisphere light): faces read near full base colour with a gentle
      // top-bright, bottom-grey vertical gradient — not a dark directional
      // falloff — so a box doesn't darken when it flips from preview to raymarch.
      // Matches the editing rig analytically: AmbientLight(1.0) + HemisphereLight
      // (sky #fff, ground #808080≈0.216 linear, intensity 2). Irradiance is
      // linear in (0.5+0.5*n.y); anchored so horizontal faces = 0.70 (measured),
      // top ≈ 0.95, bottom ≈ 0.45.
      float hemi = 0.5 + 0.5 * nrm.y;
      vec3 col = base * mix(0.45, 0.95, hemi);
      // Surface uv = the two in-plane axes of the cell-entry hit point; both the
      // grid and the silhouette edges key off it.
      vec3 hitPos = ro + rds * tHit;
      vec2 uv = abs(nrm.x) > 0.5 ? hitPos.yz
              : abs(nrm.y) > 0.5 ? hitPos.xz
                                 : hitPos.xy;
      // Grid lines: solid 0x404040 gray, edges: solid black — matching the
      // mesher's GRID_LINE_COLOR / EDGE_LINE_COLOR (in linear space; sRGB
      // 0x404040 ≈ 0.051 linear). Blend by the AA line coverage. A SELECTED cell
      // instead draws its gridlines solid white — the selection indicator, drawn
      // right here on the hit surface so it's occluded exactly like the voxel
      // face (a separate overlay line mesh z-fights the raymarched depth and
      // bleeds through faces when zoomed out). Selected lines show even with the
      // grid toggled off, and skip the zoom fade so the selection stays crisp.
      float selCell = (uHasSelection > 0.5)
        ? texture(uSelection, (vec3(cell) + 0.5) / uTexDims).r : 0.0;
      if (uShowGrid > 0.5 || selCell > 0.5) {
        // 3x3 supersample: a 1px line sampled once per pixel breaks into dashes
        // along its length; averaging 9 sub-positions keeps it thin AND solid
        // (the MSAA the mesh's geometry lines get for free).
        vec2 sdx = dFdx(uv) / 3.0;
        vec2 sdy = dFdy(uv) / 3.0;
        float g = 0.0;
        for (int sx = -1; sx <= 1; sx++)
          for (int sy = -1; sy <= 1; sy++)
            g += gridFactor(uv + sdx * float(sx) + sdy * float(sy));
        float cov = g / 9.0;
        if (selCell > 0.5) col = mix(col, vec3(1.0), cov);
        else               col = mix(col, vec3(0.051), cov * uGridFade);
      }
      // Silhouette edges: a darker line where the hit face meets the model's
      // boundary — the two in-plane axes are checked against the mesher's rule.
      // Skipped on selected cells: their boundary is already solid white from the
      // selection pass, and a black edge over it would make a selected voxel look
      // different with edges on vs off — the selection should read identically
      // either way.
      if (uShowEdges > 0.5 && selCell < 0.5) {
        ivec3 Ni = ivec3(nrm);
        ivec3 a1, a2;
        if (abs(nrm.x) > 0.5) { a1 = ivec3(0, 1, 0); a2 = ivec3(0, 0, 1); }
        else if (abs(nrm.y) > 0.5) { a1 = ivec3(1, 0, 0); a2 = ivec3(0, 0, 1); }
        else { a1 = ivec3(1, 0, 0); a2 = ivec3(0, 1, 0); }
        vec2 fw = fwidth(uv);
        float e = max(edgeAxis(cell, Ni, a1, uv.x, fw.x),
                      edgeAxis(cell, Ni, a2, uv.y, fw.y));
        col = mix(col, vec3(0.0), e * uEdgeFade);
      }
      // Write the TRUE hit-surface depth: the box geometry only gives its far
      // face, so without this the model never occludes other scene geometry (the
      // cage shows through voxels, etc.). Small forward bias wins the depth test
      // against the work-volume walls a boundary-filling voxel coincides with.
      vec3 hitLocal = hitPos * uCellSize;
      vec4 mvPos = modelViewMatrix * vec4(hitLocal, 1.0);
      vec4 clip = projectionMatrix * mvPos;
      outViewZ = -mvPos.z;
      outDepth = (clip.z / clip.w) * 0.5 + 0.5 - 1e-4;
      outCol = col; // LINEAR; main() averages sub-samples then encodes sRGB once
      return true;
    }
    if (tMax.x < tMax.y && tMax.x < tMax.z) {
      tHit = tMax.x; cell.x += int(stp.x); tMax.x += tDelta.x; nrm = vec3(-stp.x, 0.0, 0.0);
    } else if (tMax.y < tMax.z) {
      tHit = tMax.y; cell.y += int(stp.y); tMax.y += tDelta.y; nrm = vec3(0.0, -stp.y, 0.0);
    } else {
      tHit = tMax.z; cell.z += int(stp.z); tMax.z += tDelta.z; nrm = vec3(0.0, 0.0, -stp.z);
    }
  }
  return false;
}

// Supersampled, coverage-anti-aliased entry. The raymarcher resolves the voxel
// surface INSIDE one box fragment, so the box geometry's MSAA can't anti-alias
// the per-voxel silhouette — where the model meets the background (the top edge,
// the side edges, any edge-on face) a single sample per pixel hard-discards or
// hits at random and aliases into a speckled band that reads as the model
// "getting culled". The mesh path stays clean because MSAA smooths its real
// geometry edges. Fix: at those pixels, trace an N×N grid of sub-pixel rays,
// average the colour of the ones that HIT, and emit the hit FRACTION as alpha so
// the edge composites over the background (partial-coverage AA, like MSAA). The
// material is transparent; interior pixels are fully covered (alpha 1) and the
// body stays solid + still writes depth.
//
// WHERE to supersample: the tell is the per-pixel HIT-DEPTH GRADIENT. It spikes
// at silhouettes (a hit pixel beside a background miss) and on grazing / edge-on
// faces (depth races across the screen) — exactly the aliasing cases — and is
// ~flat on face-on / zoomed-in surfaces, which a single sample already nails. An
// earlier version gated on the box BACK-FACE footprint, which is unrelated to the
// visible surface, so it single-sampled silhouettes and the speckle survived.
//
// The gradient must be measured ZOOM-INVARIANTLY: view-space depth change across
// the pixel, divided by the pixel's lateral footprint AT THE HIT. That ratio is
// pure surface orientation (≈ tan of the hit face's tilt away from face-on) plus
// a spike at any depth discontinuity, so the classification doesn't change as
// the camera closes in. An earlier version gated on the raw NDC-depth gradient
// with a fixed 1e-4 threshold; NDC depth steepens like ~1/z² near the eye, so a
// deep zoom reclassified most of a face-on model as "grazing" and paid the 16-ray
// path across the whole canvas — frame rate cratered exactly when the model
// filled the screen.
void main() {
  // Centre ray first — every pixel, uniform control flow (cheap on flat surfaces).
  vec3 col0;
  float d0 = 1.0;
  float vz0 = 0.0;
  bool hit0 = traceFrag(vLocalPos, col0, d0, vz0);
  if (!hit0) { d0 = 1.0; vz0 = -1e5; } // miss sentinel: silhouette quads spike the gradient

  // Pixel footprint on the box surface (sub-rays spread across exactly this), in
  // metres; projected to the hit depth for perspective (rays diverge), used as-is
  // for ortho (rays are parallel, so the footprint is depth-independent).
  vec3 dpx = dFdx(vLocalPos);
  vec3 dpy = dFdy(vLocalPos);
  float fpBox = max(length(dpx), length(dpy));
  float vzBox = -(modelViewMatrix * vec4(vLocalPos, 1.0)).z;
  float fp = (uOrtho > 0.5) ? fpBox : fpBox * max(vz0, 0.0) / max(vzBox, 1e-4);

  float dvz = max(abs(dFdx(vz0)), abs(dFdy(vz0))); // view-depth change across the pixel
  // 4.0: supersample once depth races ≥4× the lateral footprint — the face is
  // within ~14° of edge-on, or the quad straddles a depth step / silhouette.
  if (dvz < 4.0 * fp + 1e-7) {
    // Flat / face-on / interior — one sample is exact. Stay opaque + cheap.
    if (!hit0) discard;
    gl_FragDepth = d0;
    outColor = vec4(linearToSRGB(col0), 1.0);
    return;
  }

  // Edge / grazing pixel: 4×4 rotated-grid supersample with coverage as alpha.
  const int grid = 4;
  vec3 acc = vec3(0.0);
  int hits = 0;
  float depth = 1.0;
  float inv = 1.0 / float(grid);
  for (int sy = 0; sy < grid; sy++) {
    for (int sx = 0; sx < grid; sx++) {
      vec2 o = (vec2(float(sx), float(sy)) + 0.5) * inv - 0.5;
      o += vec2(0.33, -0.17) * inv; // rotate to decorrelate the axes
      vec3 fl = vLocalPos + dpx * o.x + dpy * o.y;
      vec3 col; float d; float vz;
      if (traceFrag(fl, col, d, vz)) { acc += col; hits++; depth = min(depth, d); }
    }
  }
  if (hits == 0) discard;   // every sub-ray missed → fully background
  gl_FragDepth = depth;     // nearest hit wins the depth test (conservative occlusion)
  outColor = vec4(linearToSRGB(acc / float(hits)), float(hits) / float(grid * grid));
}
`;

export class VoxelRaymarchView {
  readonly mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  private volumeTex: THREE.Data3DTexture | null = null;
  private paletteTex: THREE.DataTexture;
  private palette: PaletteLookup;
  private vol: VolumeData | null = null;
  private coarseTex: THREE.Data3DTexture | null = null;
  private coarse: CoarseData | null = null;
  private lastVersion = -1;
  // Selection overlay: a volume-shaped R8 texture (255 = selected) the shader
  // reads to recolor a hit cell's gridlines white. Rebuilt only when the
  // selection version or the volume dims/origin change.
  private selectionTex: THREE.Data3DTexture;
  private selData: Uint8Array | null = null;
  private lastSelVersion = -1;
  private selTexW = -1;
  private selTexH = -1;
  private selTexD = -1;
  private selOx = 0;
  private selOy = 0;
  private selOz = 0;

  constructor(palette: PaletteLookup) {
    this.palette = palette;
    const palData = buildPaletteData(palette);
    this.paletteTex = new THREE.DataTexture(palData, 256, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.paletteTex.minFilter = THREE.NearestFilter;
    this.paletteTex.magFilter = THREE.NearestFilter;
    this.paletteTex.needsUpdate = true;

    // 1×1×1 placeholder so uSelection is always a bound sampler; the first
    // syncSelection with a real volume replaces it (selTex dims start at -1).
    this.selectionTex = this.makeR8Tex(new Uint8Array(1), 1, 1, 1);

    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.BackSide, // always get a fragment, even from inside the box
      // Depth comes from gl_FragDepth (the true hit surface), so polygonOffset
      // (which biases the rasterized depth, not gl_FragDepth) no longer applies —
      // the forward bias lives in the shader instead.
      // `transparent` enables the silhouette coverage-AA: edge fragments emit
      // alpha < 1 and composite over the scene behind them. depthWrite stays ON
      // (three.js respects it for transparent materials) so the solid body still
      // occludes the cage/scene via gl_FragDepth, exactly as before.
      transparent: true,
      depthWrite: true,
      uniforms: {
        uVolume: { value: null },
        uPalette: { value: this.paletteTex },
        uCameraLocal: { value: new THREE.Vector3() },
        uOrtho: { value: 0 },
        uViewDirLocal: { value: new THREE.Vector3(0, 0, -1) },
        uDims: { value: new THREE.Vector3() },
        uTexDims: { value: new THREE.Vector3() },
        uModelMin: { value: new THREE.Vector3() },
        uModelMax: { value: new THREE.Vector3() },
        uCoarse: { value: null },
        uCoarseTexDims: { value: new THREE.Vector3() },
        uChunkOrigin: { value: new THREE.Vector3() },
        uVolumeOrigin: { value: new THREE.Vector3() },
        uCellSize: { value: CELL_SIZE },
        uLightDir: { value: new THREE.Vector3(0.4, 1.0, 0.6).normalize() },
        uShowGrid: { value: 1 },
        uShowEdges: { value: 1 },
        uGridFade: { value: 1 },
        uEdgeFade: { value: 1 },
        uSelection: { value: this.selectionTex },
        uHasSelection: { value: 0 },
      },
    });
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.material);
    this.mesh.frustumCulled = false;
  }

  private makeR8Tex(data: Uint8Array, w: number, h: number, d: number): THREE.Data3DTexture {
    const tex = new THREE.Data3DTexture(data, w, h, d);
    tex.format = THREE.RedFormat;
    tex.type = THREE.UnsignedByteType;
    tex.minFilter = THREE.NearestFilter;
    tex.magFilter = THREE.NearestFilter;
    tex.wrapS = tex.wrapT = tex.wrapR = THREE.ClampToEdgeWrapping;
    tex.unpackAlignment = 1;
    tex.needsUpdate = true; // force the GPU upload
    return tex;
  }

  private makeVolumeTexture(v: VolumeData): THREE.Data3DTexture {
    return this.makeR8Tex(v.data, v.texW, v.h, v.d);
  }

  // Coarse per-chunk occupancy texture: rebuilt every edit (tiny), reused unless
  // its dims change (work-volume resize).
  private syncCoarse(grid: VoxelGrid, bounds: CellBounds): void {
    const prev = this.coarse;
    const nc = buildCoarseData(grid, bounds, prev?.data);
    const dimsChanged =
      prev === null || prev.texCw !== nc.texCw || prev.ch !== nc.ch || prev.cd !== nc.cd;
    this.coarse = nc;
    if (dimsChanged || this.coarseTex === null) {
      this.coarseTex?.dispose();
      this.coarseTex = this.makeR8Tex(nc.data, nc.texCw, nc.ch, nc.cd);
      this.material.uniforms.uCoarse.value = this.coarseTex;
    } else {
      this.coarseTex.needsUpdate = true;
    }
    (this.material.uniforms.uCoarseTexDims.value as THREE.Vector3).set(nc.texCw, nc.ch, nc.cd);
    (this.material.uniforms.uChunkOrigin.value as THREE.Vector3).set(nc.cox, nc.coy, nc.coz);
    (this.material.uniforms.uVolumeOrigin.value as THREE.Vector3).set(
      bounds.minIx, bounds.minIy, bounds.minIz,
    );
  }

  // Push the grid into the 3D texture. The whole point of #3: an edit is a
  // texture update, not a re-mesh. On the first call (or a work-volume resize)
  // it full-rebuilds + rebuilds the box geometry + (re)creates the texture; on
  // an ordinary edit it re-reads ONLY the dirtied chunks (O(edit), not
  // O(volume)) into the SAME buffer and flags needsUpdate — reusing the texture
  // object so we don't churn GPU allocations every edit (that caused a creeping
  // lag after repeated draws).
  // Returns what it drained from the grid's ONE-SHOT dirty-chunk queue so the
  // caller can hand it on: the render loop keeps the mesher warm in the same
  // frame (renderer hot-swap by zoom), and `consumeDirtyChunks` has a single
  // consumer — without the handoff the mesher never sees another edit. "all"
  // = a full rebuild happened (resize/first sync): re-feed every chunk.
  syncVolume(grid: VoxelGrid, bounds: CellBounds): Set<number> | "all" | null {
    const w = Math.max(0, bounds.maxIx - bounds.minIx);
    const h = Math.max(0, bounds.maxIy - bounds.minIy);
    const d = Math.max(0, bounds.maxIz - bounds.minIz);
    const prev = this.vol;
    const dimsChanged =
      prev === null || prev.w !== w || prev.h !== h || prev.d !== d ||
      prev.ox !== bounds.minIx || prev.oy !== bounds.minIy || prev.oz !== bounds.minIz;
    if (!dimsChanged && grid.version === this.lastVersion) return null;
    this.lastVersion = grid.version;

    // Occupied-AABB clip bounds (volume cell space) — the empty-space skip.
    const cb = grid.occupiedChunkBounds();
    const min = this.material.uniforms.uModelMin.value as THREE.Vector3;
    const max = this.material.uniforms.uModelMax.value as THREE.Vector3;
    if (cb === null) {
      min.set(0, 0, 0);
      max.set(0, 0, 0); // empty model → DDA does nothing
    } else {
      min.set(
        Math.max(0, cb.x0 - bounds.minIx),
        Math.max(0, cb.y0 - bounds.minIy),
        Math.max(0, cb.z0 - bounds.minIz),
      );
      max.set(
        Math.min(w, cb.x1 + 1 - bounds.minIx),
        Math.min(h, cb.y1 + 1 - bounds.minIy),
        Math.min(d, cb.z1 + 1 - bounds.minIz),
      );
    }
    this.syncCoarse(grid, bounds);

    if (dimsChanged) {
      const nv = buildVolumeData(grid, bounds, prev?.data);
      this.vol = nv;
      grid.consumeDirtyChunks(); // the full rebuild already covers these
      this.uploadVolume(nv);
      (this.material.uniforms.uDims.value as THREE.Vector3).set(nv.w, nv.h, nv.d);
      (this.material.uniforms.uTexDims.value as THREE.Vector3).set(nv.texW, nv.h, nv.d);

      // Box spanning the volume, min corner at local origin, positioned at the
      // volume's min corner so mesh-local cell space = texel space.
      this.mesh.geometry.dispose();
      const geo = new THREE.BoxGeometry(nv.w * CELL_SIZE, nv.h * CELL_SIZE, nv.d * CELL_SIZE);
      geo.translate((nv.w * CELL_SIZE) / 2, (nv.h * CELL_SIZE) / 2, (nv.d * CELL_SIZE) / 2);
      this.mesh.geometry = geo;
      this.mesh.position.set(nv.ox * CELL_SIZE, nv.oy * CELL_SIZE, nv.oz * CELL_SIZE);
      return "all";
    }

    const v = prev as VolumeData;
    const dirty = grid.consumeDirtyChunks();
    for (const ck of dirty) {
      const [cx, cy, cz] = parseChunkKey(ck);
      updateVolumeChunk(v, grid, cx, cy, cz);
    }
    // Reuse the texture (its image.data IS v.data, mutated in place above) — just
    // re-upload. No dispose/recreate, so no per-edit GPU allocation churn.
    if (this.volumeTex) this.volumeTex.needsUpdate = true;
    return dirty;
  }

  private uploadVolume(v: VolumeData): void {
    this.volumeTex?.dispose();
    this.volumeTex = this.makeVolumeTexture(v);
    this.material.uniforms.uVolume.value = this.volumeTex;
  }

  // Mirror the live selection into the overlay texture so the shader can paint
  // selected cells' gridlines white. Cheap: skips when neither the selection nor
  // the volume layout changed; on a change it zeroes the buffer and stamps the
  // (usually small) selected cells. Must run AFTER syncVolume each frame so
  // this.vol reflects the current volume dims/origin.
  syncSelection(selection: Selection): void {
    const v = this.vol;
    if (v === null) {
      this.material.uniforms.uHasSelection.value = 0;
      return;
    }
    const dimsChanged =
      v.texW !== this.selTexW || v.h !== this.selTexH || v.d !== this.selTexD ||
      v.ox !== this.selOx || v.oy !== this.selOy || v.oz !== this.selOz;
    if (!dimsChanged && selection.version === this.lastSelVersion) return;
    this.lastSelVersion = selection.version;

    const n = v.texW * v.h * v.d;
    let data = this.selData;
    if (data === null || data.length < n) {
      data = new Uint8Array(n);
      this.selData = data;
    }
    data.fill(0, 0, n);
    const size = selection.size();
    if (size > 0) {
      for (const c of selection.entries()) {
        const x = c.ix - v.ox, y = c.iy - v.oy, z = c.iz - v.oz;
        if (x < 0 || x >= v.w || y < 0 || y >= v.h || z < 0 || z >= v.d) continue;
        data[x + v.texW * (y + v.h * z)] = 255; // same layout as the volume tex
      }
    }
    this.material.uniforms.uHasSelection.value = size > 0 ? 1 : 0;

    // Recreate the texture only when its dims change (Data3DTexture dims are
    // fixed); otherwise re-upload the mutated buffer in place.
    if (v.texW !== this.selTexW || v.h !== this.selTexH || v.d !== this.selTexD) {
      this.selectionTex.dispose();
      this.selectionTex = this.makeR8Tex(data, v.texW, v.h, v.d);
      this.material.uniforms.uSelection.value = this.selectionTex;
    } else {
      this.selectionTex.needsUpdate = true;
    }
    this.selTexW = v.texW; this.selTexH = v.h; this.selTexD = v.d;
    this.selOx = v.ox; this.selOy = v.oy; this.selOz = v.oz;
  }

  // Force the next syncVolume to do a FULL rebuild from the grid (drops the
  // incremental state). Used when becoming the active renderer again, since the
  // mesh path may have edited the grid while we were dormant.
  forceRebuild(): void {
    this.vol = null;
    this.lastVersion = -1;
    this.lastSelVersion = -1; // re-stamp the selection overlay on re-activation
  }

  // Per frame: the shader needs the camera in the mesh's local space. Perspective
  // uses the eye point (uCameraLocal); orthographic uses a constant view
  // direction (uViewDirLocal) so the rays stay parallel — otherwise panning the
  // ortho camera shifts the eye sideways and the divergent perspective rays
  // reintroduce foreshortening (the "no longer true orthographic" bug).
  updateCamera(camera: THREE.Camera): void {
    this.mesh.updateWorldMatrix(true, false);
    const local = (this.material.uniforms.uCameraLocal.value as THREE.Vector3).copy(camera.position);
    this.mesh.worldToLocal(local);
    const ortho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    this.material.uniforms.uOrtho.value = ortho ? 1 : 0;
    if (ortho) {
      const dir = (this.material.uniforms.uViewDirLocal.value as THREE.Vector3);
      camera.getWorldDirection(dir); // world-space forward (-Z), normalized
      _invMeshMat.copy(this.mesh.matrixWorld).invert();
      dir.transformDirection(_invMeshMat).normalize(); // → mesh-local direction
    }
  }

  refreshPalette(): void {
    buildPaletteData(this.palette, this.paletteTex.image.data as Uint8Array);
    this.paletteTex.needsUpdate = true;
  }

  setShowGrid(show: boolean): void {
    this.material.uniforms.uShowGrid.value = show ? 1 : 0;
  }

  setShowEdges(show: boolean): void {
    this.material.uniforms.uShowEdges.value = show ? 1 : 0;
  }

  setLineFade(gridFade: number, edgeFade: number): void {
    this.material.uniforms.uGridFade.value = gridFade;
    this.material.uniforms.uEdgeFade.value = edgeFade;
  }

  dispose(): void {
    this.volumeTex?.dispose();
    this.paletteTex.dispose();
    this.selectionTex.dispose();
    this.material.dispose();
    this.mesh.geometry.dispose();
  }
}
